import { useEffect, useRef, useState } from 'react';

/**
 * @description This hook is used to return mount state of component,
 * It can be used wherever we want to separate visibility state from mount state.
 * Whenever we conditionally render a component, we need to unmount it only after our closing animations has run. i.e after a certain delay
 * This hook abstracts that logic and will unmount the component after the specified delay
 *
 * @param {boolean} isVisible - State to control visibility of component to conditionally apply close and open animations.
 * @param {number} unmountDelay - Delay (ms) after which component will be unmounted, this is the time taken by closing animations to run.
 *
 * @returns mount state.
 *
 * @example
 * const {isMounted} = useDelayedUnmount(isOpen,300)
 */
export const useDelayedUnmount = (isVisible: boolean, unmountDelay: number) => {
  const [isMounted, setIsMounted] = useState<boolean>(isVisible);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
    } else {
      timeoutRef.current = setTimeout(() => {
        setIsMounted(false);
      }, unmountDelay);
    }
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [isVisible, unmountDelay]);

  return { isMounted };
};
